export interface SatelliteTrackQueuePoint {
  time?: string;
  latitude?: number | string;
  longitude?: number | string;
  lat?: number | string;
  lon?: number | string;
  lng?: number | string;
}

export interface SatelliteTrackPoint {
  latitude: number;
  longitude: number;
  xSeconds: number;
  timeIso: string;
}

export interface SatelliteTrackWindow {
  playbackHeadTimeMs: number;
  windowSeconds: number;
  points: SatelliteTrackPoint[];
}

function toTimestampMs(time?: string): number | null {
  if (!time) {
    return null;
  }

  const normalizedTime = time.includes('T') ? time : time.replace(' ', 'T');
  const hasTimezone = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(normalizedTime);
  const utcTime = hasTimezone ? normalizedTime : `${normalizedTime}Z`;
  const parsed = Date.parse(utcTime);
  return Number.isFinite(parsed) ? parsed : null;
}

function clampLatitude(latitude: number): number {
  return Math.max(-85, Math.min(85, latitude));
}

function normalizeLongitude(longitude: number): number {
  let normalized = longitude;
  while (normalized <= -180) normalized += 360;
  while (normalized > 180) normalized -= 360;
  return normalized;
}

function parseCoordinate(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function buildSatelliteTrackWindow(
  processedDataQueue: SatelliteTrackQueuePoint[],
  playbackHeadTimeIso: string | null,
  windowSeconds: number
): SatelliteTrackWindow {
  const safeWindowSeconds = Number.isFinite(windowSeconds) && windowSeconds > 0 ? windowSeconds : 120;
  const headMs = (playbackHeadTimeIso ? toTimestampMs(playbackHeadTimeIso) : null) ?? Date.now();
  const halfWindowSeconds = safeWindowSeconds / 2;

  const points: SatelliteTrackPoint[] = [];
  for (const row of processedDataQueue) {
    const pointMs = toTimestampMs(row?.time);
    if (pointMs === null) {
      continue;
    }

    const candidateLatitude = parseCoordinate(row?.latitude ?? row?.lat);
    const candidateLongitude = parseCoordinate(row?.longitude ?? row?.lon ?? row?.lng);
    if (candidateLatitude === null || candidateLongitude === null) {
      continue;
    }

    let latitude = candidateLatitude;
    let longitude = candidateLongitude;

    // Some feeds swap lat/lon. If values strongly indicate a swap, correct it.
    if (Math.abs(latitude) > 90 && Math.abs(longitude) <= 90) {
      latitude = candidateLongitude;
      longitude = candidateLatitude;
    }

    if (Math.abs(latitude) > 90 || Math.abs(longitude) > 1800) {
      continue;
    }

    const xSeconds = (pointMs - headMs) / 1000;
    if (xSeconds < -halfWindowSeconds || xSeconds > halfWindowSeconds) {
      continue;
    }

    points.push({
      latitude: clampLatitude(latitude),
      longitude: normalizeLongitude(longitude),
      xSeconds,
      timeIso: new Date(pointMs).toISOString(),
    });
  }

  points.sort((a, b) => a.xSeconds - b.xSeconds);

  return {
    playbackHeadTimeMs: headMs,
    windowSeconds: safeWindowSeconds,
    points,
  };
}
